import React, { useEffect, useRef, useState } from 'react';

// v17.0.2
import { extent } from 'd3-array'; // v^2.12.1
import { format } from 'd3-format'; // v^2.0.0
import { scaleLinear, scaleTime } from 'd3-scale'; // v^3.2.4
import { pointer } from 'd3-selection'; // v^2.0.0
import '../styles/line-chart.scss';
import { useCallback } from 'react';
import { formatAsIndianCurrency } from '../utils/currency';
import { isMobile } from '../components/quick-links/faq';
import { isMobileDevice } from '../utils/constants';
import AsyncLoader from './AsyncLoader';

const height = 300;
const hoverBoxWidth = 120;
const strokeColor = 'rgb(49 178 103)';

enum Intervals {
    one_month = 30,
    six_months = 6 * 30,
    one_year = 12 * 30,
    three_years = 3 * 12 * 30,
    all = 0,
}

const options: {
    title: string;
    value: Intervals;
    format?: (value: Date) => string;
    ticks?: number;
}[] = [
        {
            title: '1M',
            value: Intervals.one_month,
            format: (d) => d.toLocaleDateString('en-In', { month: 'short', day: 'numeric' }),
            ticks: 30,
        },
        {
            title: '6M',
            value: Intervals.six_months,
            format: (d) => d.toLocaleDateString('en-In', { year: '2-digit', month: 'short' }),
            ticks: 6,
        },
        {
            title: '1Yr',
            value: Intervals.one_year,
            format: (d) => d.toLocaleDateString('en-In', { year: '2-digit', month: 'short' }),
            ticks: 12,
        },
        {
            title: '3Yr',
            value: Intervals.three_years,
            format: (d) => d.toLocaleDateString('en-In', { year: 'numeric' }),
            ticks: 3,
        },
        {
            title: 'All',
            value: Intervals.all,
            format: (d) => d.toLocaleDateString('en-In', { year: 'numeric' }),
        },
    ];

export default (props: any): JSX.Element => {
    const [intervalData, setIntervalData] = useState<any>([]);
    const [time, setTime] = useState<Intervals>(Intervals.all);

    useEffect(() => {
        setIntervalData(props.data);
    }, [props.data.length]);

    const filterByTime = (time: Intervals) => {
        if (time === Intervals.all) {
            return props.data;
        }
        let endDate = new Date().setDate(new Date().getDate() - time);
        return props.data.filter(([time]: any) => {
            return time > endDate;
        });
    };

    const _onSelectTime = (interval: Intervals) => () => {
        if (interval === time) {
            return; // restricting applying same filter again
        }
        let data = filterByTime(interval);
        setIntervalData(data);
        setTime(interval);
        return;
    };

    const ref = useRef<any>(null);

    return (
        <div
            ref={(ele) => (ref.current = ele)}
            style={{ height: height + 80 }}
            className="line__graph"
        >
            {!props.loading && !props?.data?.length ? (
                <div> No nav data found </div>
            ) : (
                <AsyncLoader data={intervalData.length ? true : null} labelWhileLoading={'Loading'}>
                    <>
                        <Scatterplot
                            width={ref.current?.offsetWidth}
                            {...props}
                            data={intervalData}
                            interval={time}
                        />
                        <div
                            style={{ width: ref.current?.offsetWidth - 100 }}
                            className="time_container"
                        >
                            <div className="line"></div>
                            <div className="time-intervals">
                                {options.map((ele, index: number) => (
                                    <div
                                        className={
                                            time === ele.value
                                                ? 'time-intervals-txt active'
                                                : 'time-intervals-txt'
                                        }
                                        key={index}
                                        onClick={_onSelectTime(ele.value)}
                                    >
                                        {ele.title}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                </AsyncLoader>
            )}
        </div>
    );
};

function useMoveHoverPoint({ hoverRef, xScale, yScale, xAccessor, yAccessor, data }: any) {
    const [hoverPoint, setHoverPoint] = useState<number[] | null>(null);
    const isTouchPad = useRef(isMobile(window.navigator.userAgent) || isMobileDevice());
    const handleMouseMove = (evt: any) => {
        const [mouseX, mouseY] = pointer(evt);
        let xPoint = {
            diffx: Math.abs(mouseX - xScale(xAccessor(data[0]))),
            x: xScale(xAccessor(data[0])),
        };
        let yPoint = { diffy: Math.abs(mouseY - yScale(data[0][1])), y: yScale([0][1]) };
        // Finding nearest point to cursor position.
        data.forEach((ele: any) => {
            const _x = xScale(xAccessor(ele));
            const _y = yScale(yAccessor(ele));
            let diffx: any = Math.abs(mouseX - _x);
            let diffy: any = Math.abs(mouseY - _y);
            if (diffx < xPoint.diffx) {
                xPoint = { ...xPoint, diffx, x: ele[0] };
                yPoint = { ...yPoint, diffy, y: ele[1] };
            }
        });
        setHoverPoint([xPoint.x, yPoint.y]);
    };

    const handleTouchMove = (evt: any) => {
        const { clientX: mouseX, clientY: mouseY } = evt.touches[0];
        let xPoint = {
            diffx: Math.abs(mouseX - xScale(xAccessor(data[0]))),
            x: xScale(xAccessor(data[0])),
        };
        let yPoint = { diffy: Math.abs(mouseY - yScale(data[0][1])), y: yScale([0][1]) };
        // Finding nearest point to cursor position.
        data.forEach((ele: any) => {
            const _x = xScale(xAccessor(ele));
            const _y = yScale(yAccessor(ele));
            let diffx: any = Math.abs(mouseX - _x);
            let diffy: any = Math.abs(mouseY - _y);
            if (diffx < xPoint.diffx) {
                xPoint = { ...xPoint, diffx, x: ele[0] };
                yPoint = { ...yPoint, diffy, y: ele[1] };
            }
        });
        setHoverPoint([xPoint.x, yPoint.y]);
    };

    useEffect(() => {
        if (!hoverRef) return;
        const handleMouseLeave = () => setHoverPoint(null);

        if (isTouchPad.current) {
            hoverRef.addEventListener('touchmove', handleTouchMove);
            hoverRef.addEventListener('touchend', handleMouseLeave);
        } else {
            hoverRef.addEventListener('mousemove', handleMouseMove);
            hoverRef.addEventListener('mouseleave', handleMouseLeave);
        }
        return () => {
            if (isTouchPad.current) {
                hoverRef.removeEventListener('touchmove', handleTouchMove);
                hoverRef.removeEventListener('touchend', handleMouseLeave);
            } else {
                hoverRef.removeEventListener('mousemove', handleMouseMove);
                hoverRef.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, [hoverRef, data]);

    return {
        hoverPoint,
    };
}

const HoverPoint = ({
    hoverPoint,
    xScale,
    xField,
    yField,
    yScale,
    labelField,
    radius = 5,
    boxWidth,
}: any) => {
    if (!hoverPoint) return null;

    const { accessor: xAccessor, formatter: xFormatter, title: xTitle } = xField;
    const { accessor: yAccessor, formatter: yFormatter, title: yTitle } = yField;

    const x = xScale(xAccessor(hoverPoint));
    const y = yScale(yAccessor(hoverPoint));
    const [yPixelMin, yPixelMax] = yScale.range();

    const hoverPosition = x + hoverBoxWidth < boxWidth ? x : x - hoverBoxWidth; // Padding the hover box for last right point position.
    return (
        <g className="pointer-events-none">
            <g data-testid="yCrosshair">
                <circle
                    cx={x}
                    cy={y}
                    r={radius}
                    fill={'white'}
                    stroke={strokeColor}
                    strokeWidth={5}
                    fillOpacity={0.8}
                />
                <line
                    y1={y - yPixelMin}
                    y2={y - yPixelMax - radius}
                    x1={x}
                    x2={x}
                    stroke={'#26b1a9'}
                    strokeDasharray={'5,5'}
                />
                <line
                    y1={y - yPixelMax + radius}
                    y2={yPixelMin}
                    x1={x}
                    x2={x}
                    stroke={'grey'}
                    strokeDasharray={'5,5'}
                />
            </g>
            <g transform={`translate(${hoverPosition + 15} ${yPixelMax})`}>
                <OutlinedSvgText
                    stroke="#fff"
                    strokeWidth={5}
                    className="text-sm font-bold"
                    dy="0.8em"
                >
                    {`${labelField} : ${formatAsIndianCurrency(yFormatter(hoverPoint[1]))}`}
                </OutlinedSvgText>
                <OutlinedSvgText
                    stroke="#fff"
                    strokeWidth={5}
                    className="text-xs"
                    dy="0.8em"
                    y={16}
                >
                    {`${xFormatter(xAccessor(hoverPoint))}`}
                </OutlinedSvgText>
            </g>
        </g>
    );
};

/** Draws a circle for each point in our data */
const Points = ({ data, xScale, yScale, xAccessor }: any) => {
    let points = 'M';
    data.forEach((coord: any) => {
        const x1 = xScale(xAccessor(coord));
        const y1 = yScale(coord[1]);
        points = points + `${x1},${y1} `;
    });
    return (
        <g data-testid="Points">
            <defs>
                <linearGradient id="grad1" x1="0%" y1="100%" x2="0%" y2="0%">
                    <stop offset="0%" stopColor="white" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#f6fdfa" stopOpacity={1} />
                </linearGradient>
            </defs>
            <path stroke={strokeColor} fill="url(#grad1)" d={points}></path>
            {/* {data.map((coord: any[], i: number) => {
                const x1 = xScale(xAccessor(coord));
                const y1 = yScale(coord[1]);
                const x2 = i < data.length - 1 ? xScale(xAccessor(data[i + 1])) : x1;
                const y2 = i < data.length - 1 ? yScale(data[i + 1][1]) : y1;
                return (
                    <>
                    
                        <line
                            x1={x1}
                            y1={y1}
                            x2={x2}
                            y2={y2}
                            strokeWidth={1.5}
                            stroke={'rgb(49 178 103)'}
                            fillOpacity={1}
                            fill={'rgb(49 178 103)'}
                            onClick={() => console.log(coord)}
                        />
                        <rect x={0} y={y2} fill="rgb(49 178 103)" />
                    </>
                );
            })} */}
        </g>
    );
};

/** fancier way of getting a nice svg text stroke */
const OutlinedSvgText = ({ stroke, strokeWidth, children, ...other }: any) => {
    return (
        <>
            <text stroke={stroke} strokeWidth={strokeWidth} {...other}>
                {children}
            </text>
            <text {...other}>{children}</text>
        </>
    );
};

/** determine number of ticks based on space available  */
function numTicksForPixels(pixelsAvailable: any, pixelsPerTick = 70) {
    return Math.floor(Math.abs(pixelsAvailable) / pixelsPerTick);
}

/** Y-axis with title  */
const YAxis = ({ yScale }: any) => {
    const [yMin, yMax] = yScale.range();
    return (
        <g data-testid="YAxis">
            <line opacity={0} x1={0} x2={0} y1={yMin} y2={yMax} stroke="var(--gray-400)" />
        </g>
    );
};

/** X-axis with title */
const XAxis = ({ xScale, formatter, innerHeight, interval }: any) => {
    const [xMin, xMax] = xScale.range();
    const numberOfGrids = options.find((item) => item.value === interval)?.ticks || 0;
    const numberOfGridsByWidth = numTicksForPixels(xMax - xMin);
    const ticks = xScale.ticks(
        Boolean(numberOfGrids) && numberOfGridsByWidth > numberOfGrids
            ? numberOfGrids
            : numberOfGridsByWidth,
    );
    return (
        <g data-testid="XAxis" transform={`translate(0 ${innerHeight})`}>
            {/* <line strokeWidth={2} x1={xMin} x2={xMax} y1={0} y2={0} stroke="var(--gray-400)" /> */}
            {/* {ticks.map((tick: any) => {
                const x = xScale(tick);
                return (
                    <g key={tick} transform={`translate(${x} 0)`}>
                        <text
                            y={10}
                            dy="0.8em"
                            textAnchor="middle"
                            fill="currentColor"
                            className="text-gray-400 text-2xs"
                        >
                            {formatter(tick)}
                        </text>
                    </g>
                );
            })} */}
        </g>
    );
};

const fields = {
    time: {
        accessor: (d: any) => new Date(new Date(d[0]).setHours(0, 0, 0, 0)),
        title: 'on',
        formatter: (value: Date) =>
            value.toLocaleDateString('en-In', { year: '2-digit', month: 'short', day: 'numeric' }),
    },
    amount: {
        accessor: (d: any) => d[1],
        title: 'Revenue',
        formatter: format('.1f'),
    },
};

export const Scatterplot = ({
    data,
    interval = Intervals.all,
    width = 600,
}: {
    data: any;
    interval: Intervals;
    width: number;
}) => {
    const margin = { top: 10, right: 50, bottom: 10, left: 50 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // read from pre-defined metric/dimension ("fields") bundles
    const xField = fields.time;
    const yField = fields.amount;

    // optionally pull out values into local variables
    const { accessor: xAccessor, title: xTitle } = xField;

    const yAccessor = yField.accessor;
    const yTitle = yField.title;
    const yFormatter = yField.formatter;

    if (!data || !data.length) {
        return <div style={{ width, height }} />;
    }

    const radius = 4;
    const xExtent: any = extent(data, xAccessor);
    const yExtent: any = extent(data, yAccessor);
    const xScale = scaleTime().domain(xExtent).range([0, innerWidth]);
    const yScale = scaleLinear().domain(yExtent).range([innerHeight, 0]);
    const svgRef = useRef(null);
    const [refNode, setRefNode] = useState<any>(null);

    const handleRect = useCallback((node) => {
        setRefNode(node);
    }, []);

    const { hoverPoint } = useMoveHoverPoint({
        hoverRef: refNode,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        data,
    });

    return (
        <svg width={width} height={height}>
            <g transform={`translate(${margin.left} ${margin.top})`}>
                <XAxis
                    xScale={xScale}
                    formatter={options.find((ele) => ele.value === interval)?.format}
                    title={xTitle}
                    innerHeight={innerHeight}
                    interval={interval}
                />
                <Points
                    radius={radius}
                    data={data}
                    xScale={xScale}
                    yScale={yScale}
                    xAccessor={xAccessor}
                    yAccessor={yAccessor}
                    hoverPoint={hoverPoint || []}
                />
                <YAxis
                    data={data}
                    yScale={yScale}
                    formatter={yFormatter}
                    title={yTitle}
                    gridLineWidth={innerWidth}
                />
                <HoverPoint
                    labelField={'NAV'}
                    xScale={xScale}
                    xField={xField}
                    yScale={yScale}
                    yField={yField}
                    hoverPoint={hoverPoint}
                    boxWidth={width - hoverBoxWidth}
                />
                <rect
                    /* this node absorbs all mouse events */
                    ref={handleRect}
                    width={innerWidth}
                    height={innerHeight}
                    x={0}
                    y={0}
                    fill="tomato"
                    fillOpacity={0}
                />
            </g>
        </svg>
    );
};
